import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Typography,
  Divider,
  styled,
  ImageList,
  ImageListItem,
  useMediaQuery,
  Skeleton,
  Container,
  Modal,
  Fade,
  Backdrop,
  Paper,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import Theme from '../../theme/Theme';
import { useParams } from 'react-router-dom';
import { PlaceService } from '../../services/place/PlaceService';
import { PlaceDetails } from '../../interfaces/PlaceDetails';
import { Collection } from '../../interfaces/Collection';
import { GridCloseIcon } from '@mui/x-data-grid';

export const PlacePage: React.FC = () => {
  const theme = Theme;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const { id } = useParams();
  const [place, setPlace] = useState<PlaceDetails>();

  useEffect(() => {
    if (id) {
      PlaceService.detail(Number(id)).then((data) => {
        setPlace(data);
      });
    }
  }, [id]);

  // Scroll para seção com hash
  useEffect(() => {
    const hash = window.location.hash.slice(1); // Remove o #
    if (hash) {
      const element = document.getElementById(hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [place]); // Rerun quando place mudar (dados carregados)

  const spaceResources = {
    'Possui ar condicionado ?': place?.hasAirConditioner ? 'Sim' : 'Não',
    'Possui buffet disponível ?': place?.hasBuffet ? 'Sim' : 'Não',
    'Possui estacionamento ?': place?.hasParking ? 'Sim' : 'Não',
    'Possui espaço ao ar livre ?': place?.hasOutdoor ? 'Sim' : 'Não',
    'Possui sonorização ?': place?.hasSound ? 'Sim' : 'Não',
    'Possui múltiplas salas ?': place?.hasRoom ? 'Sim' : 'Não',
  };

  const spaceDetails = {
    'Capacidade de Público': `mínima ${place?.minxCapacity} e máxima ${place?.maxCapacity}`,
  };

  const coloByIndex = (index: number) => {
    return allowedColors[index % allowedColors.length];
  };

  const allowedColors = [
    '#1976D2',
    '#EF6C00',
    '#00ACC1',
    '#EC407A',
    '#AED581',
    '#1976D2',
    '#EF6C00',
    '#00ACC1',
  ];

  const eventTypes =
    place?.placeEventTypes &&
    place?.placeEventTypes.map((event, index) => {
      return {
        label: event.eventTypeName,
        color: coloByIndex(index),
      };
    });

  const HeaderWrapper = styled(Box)(() => ({
    position: 'relative',
    width: '100%',
    height: isTablet ? 400 : 500,
    overflow: 'hidden',
    borderRadius: 0,
  }));

  const HeaderImage = styled('img')(() => ({
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    backgroundSize: '110%',
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    transition: 'transform 0.8s ease-in-out',
    '&:hover': {
      transform: 'scale(1.05)', // dá um zoom suave
    },
  }));

  const BrandAvatar = styled('img')(() => ({
    width: isTablet ? 200 : 250,
    marginBottom: 20,
  }));

  const [open, setOpen] = React.useState(false);
  const [colletionSelected, setColletionSelected] = React.useState<Collection>();

  const masonryRef = useRef<HTMLDivElement | null>(null);
  const masonryHeight = 445;

  const [zoomImg, setZoomImg] = React.useState<string | null>(null);

  const [zoomed, setZoomed] = useState(false);

  const handleToggleZoom = (e: React.MouseEvent) => {
    e.stopPropagation(); // impede que o clique feche o modal
    setZoomed((prev) => !prev);
  };

  const handleOpen = (collection: Collection) => {
    setColletionSelected(collection);
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setZoomImg(null);
  };

  const openZoom = (imgSrc: string) => setZoomImg(imgSrc);
  const closeZoom = () => {
    setZoomImg(null);
    setZoomed(false);
  };

  function toEmbedUrl(url: string): string {
    // Caso seja link curto (youtu.be/VIDEO_ID)
    if (url.includes('youtu.be')) {
      const videoId = url.split('youtu.be/')[1];
      return `https://www.youtube.com/embed/${videoId}`;
    }

    // Caso seja link normal (youtube.com/watch?v=VIDEO_ID)
    if (url.includes('watch?v=')) {
      const videoId = url.split('watch?v=')[1].split('&')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }

    return url; // fallback
  }

  return (
    <>
      {place ? (
        <Box bgcolor="#fff" p={isTablet ? 0 : 5} textAlign="center">
          {window.history.length > 1 && (
            <Box px={2} textAlign="left" display="flex" flexDirection={'column'}>
              <Box>
                <Button
                  variant="text"
                  size="medium"
                  sx={{ mb: 1, borderColor: '#1E88E5', color: '#1E88E5', fontWeight: 'bold' }}
                  onClick={() => window.history.back()}
                >
                  Voltar
                </Button>
              </Box>
            </Box>
          )}
          <Box
            px={2}
            display={'flex'}
            justifyContent={'space-between'}
            flexDirection={isTablet ? 'column' : 'row-reverse'}
          >
            <Box display="flex" flexDirection={'column'}>
              <Box display="flex" flexDirection="column" alignItems="center">
                <BrandAvatar src={place?.brand?.url} />
                <Button
                  variant="outlined"
                  size="medium"
                  sx={{
                    mt: 2,
                    mb: 2,
                    borderColor: '#1E88E5',
                    color: '#1E88E5',
                    fontWeight: 'bold',
                  }}
                >
                  Consultar Interesse
                </Button>
                <Box display="flex" gap={1}>
                  {['#FF5722', '#FFEB3B', '#4CAF50', '#03A9F4', '#3F51B5'].map((color, i) => (
                    <StarIcon key={i} sx={{ color, fontSize: 24 }} />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" mt={0.5}>
                  Visualizar Comentários
                </Typography>
              </Box>
            </Box>

            <Box
              p={2}
              display="flex"
              flexDirection={'column'}
              textAlign="left"
              justifyContent={'center'}
            >
              <Typography variant="h3" color="#1E88E5" fontWeight="bold">
                {place?.fantasyName}
              </Typography>
              <Typography variant="h5" color="#64B5F6" fontWeight="medium">
                {place?.placeEstabilishmentTypes
                  .map((type) => type.estabilishmentTypeName)
                  .join(', ')}
              </Typography>
              <Typography variant="body1" color={theme.palette.secondary.main} fontWeight="medium">
                {place?.districtName}, {place?.cityName}
              </Typography>
            </Box>
          </Box>
          <Box
            p={isMobile ? 0 : 2}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
              mx: 'auto',
            }}
          >
            {!!place?.banner && (
              <HeaderWrapper>
                <HeaderImage
                  src={place?.banner?.url}
                  alt="Header"
                  onClick={() => openZoom(place?.banner?.url)}
                />
              </HeaderWrapper>
            )}

            <Box mt={10} px={2}>
              <Typography variant="h4" color="orange" fontWeight="bold" textAlign="center" mb={5}>
                Informações Importantes
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: 4,
                  justifyContent: 'space-around',
                }}
              >
                {/* Capacidade Seção */}
                <Box sx={{ flex: 1, minWidth: 150 }}>
                  <Typography
                    variant="h6"
                    color="#1E88E5"
                    fontWeight="bold"
                    mb={3}
                    textAlign="center"
                  >
                    Capacidade
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box textAlign="center">
                      <Typography
                        variant="caption"
                        color="#666"
                        sx={{ fontSize: 11, fontWeight: 600 }}
                      >
                        MÍNIMA
                      </Typography>
                      <Typography variant="h4" color="#4CAF50" fontWeight="bold" sx={{ mt: 0.5 }}>
                        {place?.minxCapacity}
                      </Typography>
                      <Typography variant="caption" color="#999" sx={{ fontSize: 10 }}>
                        pessoas
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Divider sx={{ width: '60%' }} />
                    </Box>
                    <Box textAlign="center">
                      <Typography
                        variant="caption"
                        color="#666"
                        sx={{ fontSize: 11, fontWeight: 600 }}
                      >
                        MÁXIMA
                      </Typography>
                      <Typography variant="h4" color="#1E88E5" fontWeight="bold" sx={{ mt: 0.5 }}>
                        {place?.maxCapacity}
                      </Typography>
                      <Typography variant="caption" color="#999" sx={{ fontSize: 10 }}>
                        pessoas
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {!isMobile && <Divider orientation="vertical" flexItem />}

                {/* Recursos Seção */}
                <Box sx={{ flex: 1, minWidth: 150 }}>
                  <Typography
                    variant="h6"
                    color="#1E88E5"
                    fontWeight="bold"
                    mb={3}
                    textAlign="center"
                  >
                    Recursos
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
                    {Object.entries(spaceResources).map(([key, value], idx) => {
                      const label = key.replace('Possui ', '').replace(' ?', '');
                      const firstLetter = label.charAt(0).toUpperCase();
                      const rest = label.slice(1);
                      return (
                        <Box key={idx} textAlign="center">
                          <Typography
                            variant="body2"
                            color="#333"
                            fontWeight="600"
                            sx={{ fontSize: 12 }}
                          >
                            {firstLetter}
                            {rest}
                          </Typography>
                          <Typography
                            variant="caption"
                            color={value === 'Sim' ? '#4CAF50' : '#838383'}
                            sx={{ fontSize: 14, fontWeight: 700, mt: 0.3 }}
                          >
                            {value}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                {!isMobile && place?.blueprint && <Divider orientation="vertical" flexItem />}

                {/* Planta Baixa Seção */}
                {place?.blueprint && (
                  <Box sx={{ flex: 1, minWidth: 150, textAlign: 'center' }}>
                    <Typography variant="h6" color="#1E88E5" fontWeight="bold" mb={2}>
                      Planta Baixa
                    </Typography>
                    <Box
                      sx={{ cursor: 'pointer', display: 'flex', justifyContent: 'center' }}
                      onClick={() => openZoom(place?.blueprint?.url)}
                    >
                      <img
                        alt={place?.blueprint?.name}
                        src={place?.blueprint?.url}
                        width="100%"
                        style={{ maxWidth: 150 }}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            <Box
              mt={5}
              bgcolor="#286DD4"
              width="100%"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              <Box p={3}>
                <Typography variant="h4" fontWeight="bold" color="white" textAlign="center">
                  Descrição do Espaço
                </Typography>
                <Box
                  mt={2}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight={200}
                >
                  <Typography
                    variant="body1"
                    color="white"
                    textAlign="center"
                    sx={{ lineBreak: 'anywhere', fontSize: 18 }}
                  >
                    {place?.description}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {eventTypes && eventTypes.length > 0 && (
              <Box mt={10} textAlign="center">
                <Typography variant="h4" fontWeight="bold" sx={{ color: '#FFA000', mb: 4 }}>
                  Eventos que Comportamos
                </Typography>
                <Box display="flex" flexWrap="wrap" justifyContent="center" gap={2}>
                  {eventTypes.map((event, index) => (
                    <Box
                      key={index}
                      px={2}
                      py={1}
                      sx={{
                        backgroundColor: event.color,
                        color: '#fff',
                        borderRadius: '20px',
                        fontSize: 13,
                        fontWeight: 600,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          opacity: 0.85,
                          transform: 'scale(1.05)',
                        },
                      }}
                    >
                      {event.label}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {place?.collections && place?.collections.length > 0 && (
              <Box mt={10} textAlign="center" id="media" sx={{ width: '100%' }}>
                <Typography variant="h4" fontWeight="bold" sx={{ color: '#FFA000' }}>
                  Galeria de Fotos
                </Typography>
                {place?.collections?.map((collection, index) => (
                  <Paper
                    elevation={0}
                    sx={{ my: 2, p: 2, borderBottom: '1px solid #f0f0f0' }}
                    key={index}
                  >
                    <Typography variant="h5" fontWeight={600} p={1} textAlign="left" width="100%">
                      {collection?.name}
                    </Typography>

                    <Box
                      display="flex"
                      flexDirection={isMobile ? 'column' : 'row'}
                      gap={1}
                      p={1}
                      ref={masonryRef}
                    >
                      {/* Imagem principal à esquerda */}

                      <Box flex={1} minWidth={isMobile ? '100%' : '50%'}>
                        <ImageList
                          variant="standard"
                          cols={1}
                          gap={1}
                          sx={{ height: !isMobile ? masonryHeight : 'auto' }}
                        >
                          <ImageListItem
                            onClick={() => handleOpen(collection)}
                            sx={{
                              cursor: 'pointer',
                              overflow: 'hidden',
                              '& img': {
                                transition: 'transform 0.3s ease',
                                width: '100%',

                                objectFit: 'cover',
                              },
                              '&:hover img': {
                                transform: 'scale(1.05)',
                              },
                            }}
                          >
                            <img
                              src={collection?.medias[0]?.url}
                              alt={collection?.name}
                              loading="lazy"
                              style={{ borderRadius: 4 }}
                            />
                          </ImageListItem>
                        </ImageList>
                      </Box>

                      {/* Imagens menores à direita */}
                      <Box flex={1} minWidth={isMobile ? '100%' : '50%'}>
                        <ImageList
                          variant="standard"
                          cols={isMobile ? 2 : collection?.medias?.length > 2 ? 2 : 1}
                          gap={1}
                          sx={{ height: !isMobile ? masonryHeight : 'auto' }}
                        >
                          {collection?.medias?.slice(1, 5).map((media, index) => {
                            const isLastVisible = index === 3 && collection?.medias?.length > 5;
                            const remaining = collection?.medias?.length - 4;

                            return (
                              <ImageListItem
                                key={index}
                                onClick={() => handleOpen(collection)}
                                sx={{
                                  cursor: 'pointer',
                                  overflow: 'hidden',
                                  position: 'relative',
                                  '& img': {
                                    transition: 'transform 0.3s ease',
                                  },
                                  '&:hover img': {
                                    transform: 'scale(1.05)',
                                  },
                                }}
                              >
                                <img
                                  src={media?.url}
                                  alt={collection?.name}
                                  loading="lazy"
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: 4,
                                  }}
                                />

                                {isLastVisible && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      width: '100%',
                                      height: '100%',
                                      bgcolor: 'rgba(0,0,0,0.5)',
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      borderRadius: 2,
                                      color: 'white',
                                      fontWeight: 'bold',
                                      fontSize: '1.2rem',
                                      backdropFilter: 'blur(2px)',
                                      flexDirection: 'column',
                                    }}
                                  >
                                    +{remaining} Fotos
                                  </Box>
                                )}
                              </ImageListItem>
                            );
                          })}
                        </ImageList>
                      </Box>
                    </Box>
                  </Paper>
                ))}

                <Modal
                  open={open}
                  onClose={handleClose}
                  closeAfterTransition
                  slots={{ backdrop: Backdrop }}
                  slotProps={{
                    backdrop: {
                      timeout: 500,
                      sx: { backgroundColor: 'rgba(0,0,0,0.7)' },
                    },
                  }}
                  aria-labelledby="modal-title"
                  aria-describedby="modal-description"
                >
                  <Fade in={open}>
                    <Box
                      sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        bgcolor: 'background.paper',
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        outline: 'none',
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          p: 4,
                          maxHeight: '100vh',
                          boxSizing: 'border-box',
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 3,
                          }}
                        >
                          <Typography id="modal-title" variant="h4" component="h2">
                            {colletionSelected?.name}
                          </Typography>
                          <Button
                            onClick={handleClose}
                            aria-label="Fechar modal"
                            sx={{
                              minWidth: 'auto',
                              padding: 0,
                              color: theme.palette.secondary.main,
                              '&:hover': {
                                backgroundColor: 'transparent',
                                color: theme.palette.secondary.dark,
                              },
                            }}
                          >
                            <GridCloseIcon />
                          </Button>
                        </Box>

                        {colletionSelected && (
                          <Box
                            sx={{
                              flexGrow: 1,
                              overflowY: 'auto',
                            }}
                          >
                            <ImageList
                              variant="masonry"
                              cols={isMobile ? 1 : 3}
                              gap={8}
                              sx={{ width: '100%' }}
                            >
                              {colletionSelected?.medias?.map((media, index) => (
                                <ImageListItem
                                  key={index}
                                  sx={{ borderRadius: 2, overflow: 'hidden', cursor: 'pointer' }}
                                  onClick={() => openZoom(media.url)}
                                >
                                  <img
                                    src={media.url}
                                    loading="lazy"
                                    alt={`Imagem ${index + 1}`}
                                    style={{
                                      width: '100%',
                                      display: 'block',
                                      objectFit: 'cover',
                                      borderRadius: 8,
                                    }}
                                  />
                                </ImageListItem>
                              ))}
                            </ImageList>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Fade>
                </Modal>
              </Box>
            )}

            {place.youtube && (
              <Box mt={10} textAlign="center" width="100%" id="youtube">
                <Paper
                  elevation={0}
                  sx={{ p: 3, textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}
                >
                  <Typography variant="h4" fontWeight="bold" sx={{ color: '#FFA000', mb: 2 }}>
                    Vídeo
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
                    Assista ao vídeo relacionado a este local para conhecer mais detalhes e
                    curiosidades.
                  </Typography>
                  <iframe
                    title="youtube"
                    width="100%"
                    height="455"
                    src={toEmbedUrl(place.youtube)}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  ></iframe>
                </Paper>
              </Box>
            )}

            {place.type === 'BASIC' ||
              (place.type === 'PREMIUM' && (
                <Box mt={10} width="100%">
                  <Paper
                    elevation={0}
                    sx={{ textAlign: 'left', p: 3, borderBottom: '1px solid #f0f0f0' }}
                    id="map"
                  >
                    <Typography variant="h4" fontWeight="bold" sx={{ color: '#FFA000', mb: 2 }}>
                      Localização
                    </Typography>
                    <Box display="flex" justifyContent="center">
                      <iframe
                        title="Localização"
                        src={`https://www.google.com/maps?q=${place?.address}, ${place?.districtName}, ${place?.cityName}&output=embed`}
                        width="100%"
                        height="300"
                        style={{ border: 0 }}
                        loading="lazy"
                        allowFullScreen
                      ></iframe>
                    </Box>
                  </Paper>
                </Box>
              ))}
          </Box>
          <Modal
            open={!!zoomImg}
            onClose={handleClose}
            closeAfterTransition
            slots={{ backdrop: Backdrop }}
            slotProps={{
              backdrop: {
                timeout: 300,
                sx: { backgroundColor: 'rgba(0,0,0,0.9)' },
              },
            }}
            aria-labelledby="zoomed-image"
          >
            <Fade in={!!zoomImg}>
              <Box
                onClick={closeZoom}
                sx={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100vw',
                  height: '100vh',
                  overflow: 'auto',
                  cursor: zoomed ? 'zoom-out' : 'zoom-in',
                  outline: 'none',
                }}
              >
                <Box
                  sx={{
                    minHeight: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 2,
                  }}
                >
                  <img
                    src={zoomImg || ''}
                    alt="Imagem ampliada"
                    onClick={handleToggleZoom}
                    style={{
                      maxWidth: zoomed ? 'none' : '90vw',
                      maxHeight: zoomed ? 'none' : '90vh',
                      minWidth: zoomed ? '90vw' : 'auto',
                      borderRadius: 0,
                      boxShadow: '0 0 10px rgba(0,0,0,0.5)',
                      userSelect: 'none',
                      display: 'block',
                    }}
                  />
                </Box>
              </Box>
            </Fade>
          </Modal>
        </Box>
      ) : (
        <Container maxWidth="md" sx={{ mt: 10 }}>
          <Skeleton />
          <Skeleton animation="wave" />
          <Skeleton animation={false} />
        </Container>
      )}
    </>
  );
};

export default PlacePage;
